import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useEffect, useState } from 'react';
import type { VpnGatewayRecord } from '@fleet-cert-console/shared';

export interface VpnApproveModalProps {
  visible: boolean;
  gateway: VpnGatewayRecord | null;
  submitting: boolean;
  error: string | null;
  onDismiss: () => void;
  onConfirm: (approvalReason: string, dryRun: boolean) => void;
}

/**
 * Approve a VPN enrollment: allocates the gateway's CGNAT address and provisions the hub peer.
 * `approvedBy` is always the signed-in console user (stamped by the BFF), never entered here.
 */
export function VpnApproveModal({ visible, gateway, submitting, error, onDismiss, onConfirm }: VpnApproveModalProps) {
  const [reason, setReason] = useState('');
  const [dryRun, setDryRun] = useState(false);

  useEffect(() => {
    if (visible) {
      setReason('');
      setDryRun(false);
    }
  }, [visible]);

  const valid = reason.trim().length > 0;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={dryRun ? 'Approve VPN enrollment (dry run)' : 'Approve VPN enrollment'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" disabled={submitting} onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={submitting}
              disabled={!valid}
              onClick={() => onConfirm(reason.trim(), dryRun)}
            >
              {dryRun ? 'Run dry run' : 'Approve'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="Approve failed">
            {error}
          </Alert>
        )}
        {gateway && (
          <Box>
            Gateway <code>{gateway.gatewayId}</code>
            {gateway.gatewayPubKey ? (
              <Box fontSize="body-s" color="text-body-secondary">
                WireGuard key reported — ready to approve.
              </Box>
            ) : (
              <Box fontSize="body-s" color="text-status-warning">
                No WireGuard key reported yet — approve will fail until the gateway answers the initiate.
              </Box>
            )}
          </Box>
        )}
        <FormField
          label="Approval reason"
          description="Recorded on the gateway row and in the console audit trail."
        >
          <Input value={reason} onChange={({ detail }) => setReason(detail.value)} placeholder="e.g. customer demo" />
        </FormField>
        <Checkbox checked={dryRun} onChange={({ detail }) => setDryRun(detail.checked)}>
          Dry run — report the group subnet without allocating an address or touching the hub
        </Checkbox>
        <Box fontSize="body-s" color="text-body-secondary">
          Approved-by is recorded as your signed-in user.
        </Box>
      </SpaceBetween>
    </Modal>
  );
}
